import type { DadosDeducao, AtrasoDoc } from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import type { ExtratoSolicitacaoDetalheResponse } from "../../../../services/ExtractService";
import { formatDateBR } from "../../../../utils/date";

export type ExtratoPreviewView = {
  valorMinimo?: number;
  valorBonus?: number;
  valorBonusExtra?: number;
  valorTotalApoioBruto?: number;
  receitaOperacional?: number;
  faixaReceitaBonus?: {
    titulo?: string;
    receitaMin?: number;
    receitaMax?: number;
    valorMinimo?: number;
    valorBonusPotencial?: number;
    valorMaximoPotencial?: number;
  };
  criterioBonus?: Array<{
    documento?: string;
    nome?: string;
    conforme?: boolean;
    percentual?: number;
    valor?: number;
  }>;
  criterioBonusExtra?: Array<{
    documento?: string;
    nome?: string;
    conforme?: boolean;
    percentual?: number;
    valor?: number;
  }>;
};

export type ValoresSolicitadosView = {
  itens: Array<{
    solicitacao: string;
    data: string;
    valor: string;
  }>;
  total: string;
  valorLiquidoApoio: string;
};

const formatBRL = (valor: number | null | undefined) =>
  Number(valor ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

export const hasDeducaoAtrasoPreenchida = (
  atraso: DadosDeducao["deducaoAtraso"] | undefined | null,
): atraso is AtrasoDoc => {
  if (!atraso || Object.keys(atraso).length === 0) return false;
  return Boolean(atraso.dataEnvio || atraso.dataLimite || atraso.dataVencimento);
};

const mapCriteriosGrupo = (grupo: { criterios?: unknown[] } | undefined) =>
  (grupo?.criterios ?? []).map((c: any) => {
    const titulo = c?.titulo ?? "";
    return {
      documento: titulo,
      nome: titulo,
      conforme: !!c?.conforme,
      percentual: c?.percentualBonus ?? 0,
      valor: c?.valor ?? 0,
    };
  });

const isGrupoBonusExtra = (grupo: { titulo?: string; tituloResumo?: string }) =>
  /extra/i.test(grupo?.titulo ?? "") || /extra/i.test(grupo?.tituloResumo ?? "");

export const mapExtratoSolicitacaoApiToPreview = (
  api: ExtratoSolicitacaoDetalheResponse,
): ExtratoPreviewView => {
  const grupos = [...(api.gruposCriteriosBonusExtrato ?? [])].sort(
    (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0),
  );
  const grupoBonus = grupos.find((g) => !isGrupoBonusExtra(g));
  const grupoBonusExtra = grupos.find((g) => isGrupoBonusExtra(g));
  const faixa = api.receitaOperacionalExtrato?.faixaReceitaBonusExtrato;

  return {
    valorMinimo: api.valorMinimoApoio,
    valorBonus: grupoBonus?.valorAlcancado,
    valorBonusExtra: grupoBonusExtra?.valorAlcancado,
    valorTotalApoioBruto: api.valorTotalApoio,
    receitaOperacional:
      api.receitaOperacionalExtrato?.valorReceitaOperacional,
    faixaReceitaBonus: faixa
      ? {
          titulo: faixa.titulo,
          receitaMin: faixa.receitaMinima,
          receitaMax: faixa.receitaMaxima,
          valorMinimo: faixa.valorMinimo,
          valorBonusPotencial: faixa.bonusPotencial,
          valorMaximoPotencial: faixa.valorMaximoPotencial,
        }
      : undefined,
    criterioBonus: mapCriteriosGrupo(grupoBonus),
    criterioBonusExtra: mapCriteriosGrupo(grupoBonusExtra),
  };
};

export const mapExtratoSolicitacaoApiToDeducoes = (
  api: ExtratoSolicitacaoDetalheResponse,
): DadosDeducao => {
  const ded = api.deducoesExtrato;
  const atraso = ded?.deducaoAtrasoEntrega;

  return {
    deducaoAtraso: atraso
      ? {
          ...(Number(atraso.id) > 0 ? { id: atraso.id } : {}),
          dataEnvio: atraso.documentacaoEnviadaEm ?? undefined,
          dataLimite: atraso.dataLimiteEntrega ?? undefined,
          dataVencimento: atraso.dataLimiteEntrega ?? undefined,
          diasAtraso: atraso.diasAtraso ?? 0,
          percentualDeducao: atraso.percentualDeducao ?? 0,
          valorTotalApoio: atraso.valorTotalApoio ?? 0,
          valorDeducao: atraso.valorDeducao ?? 0,
          deduzir: !!atraso.deduzir,
        }
      : {},
    deducaoTitulos: (ded?.deducaoTitulosEmAberto?.titulos ?? []).map((t) => ({
      id: t.id,
      recno: t.recno,
      filial: t.filial ?? "",
      titulo: t.titulo ?? "",
      parcela: t.parcela ?? "",
      natureza: t.natureza ?? "",
      dataEmissao: t.emitidoEm ?? undefined,
      dataVencimento: t.vencimentoEm ?? undefined,
      atrasoDias: t.diasAtraso ?? 0,
      valor: t.valor ?? 0,
      historico: t.historico ?? "",
      deduzir: !!t.deduzir,
    })),
    deducaoOutros: (ded?.deducaoOutros?.deducoes ?? []).map((d) => ({
      ...(Number(d.id) > 0 ? { id: d.id } : {}),
      descricao: d.descricao ?? "",
      valor: d.valor ?? 0,
      deduzir: !!d.deduzir,
    })),
  };
};

export const mapExtratoSolicitacaoApiToValoresSolicitados = (
  api: ExtratoSolicitacaoDetalheResponse,
): ValoresSolicitadosView | null => {
  const bloco = api.valoresJaSolicitadosExtrato;
  if (!bloco) return null;

  const itens = (bloco.solicitacoes ?? []).map((s) => ({
    solicitacao: s.solicitacao ?? "—",
    data: formatDateBR(s.solicitadoEm ?? undefined),
    valor: formatBRL(s.valorApoiado),
  }));

  return {
    itens,
    total: formatBRL(bloco.valorTotalSolicitado),
    valorLiquidoApoio: formatBRL(api.valorLiquidoApoio),
  };
};
